/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { Dropdown } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import { useCallback, useMemo } from 'react'
import Text from 'src/components/ts-components/Text'
import Icon from 'src/components/Icon'
import { CheckOutlined } from '@ant-design/icons'

type StreamType = 'league' | 'home' | 'visitor'

interface StreamState {
  type: StreamType
  livestreamId: string | null
  broadcastState: string
  streamUrl: string | null
  broadcastId: string | null
  isLive: boolean
  wasLive: boolean
  hasRecording: boolean
  recordingUrl: string | null
}

interface StreamSelectorDropdownProps {
  streams: StreamState[]
  selectedStreamType: StreamType | null
  onSelectStream: (streamType: StreamType) => void
  homeTeamName: string
  visitorTeamName: string
  homeTeamImage: string | null
  visitorTeamImage: string | null
  organizationName?: string
  isMobile?: boolean
}

const PLACEHOLDER_ORGANIZATION =
  '/images/generic_placeholders/placeholder_organization.png'
const PLACEHOLDER_TEAM = '/images/generic_placeholders/placeholder_team.png'

/**
 * Get display label for a stream type
 */
const getStreamLabel = (
  streamType: StreamType,
  homeTeamName: string,
  visitorTeamName: string,
  organizationName?: string
): string => {
  switch (streamType) {
    case 'home':
      return homeTeamName || 'Home'
    case 'visitor':
      return visitorTeamName || 'Visitor'
    case 'league':
    default:
      return organizationName || 'League'
  }
}

/**
 * Filter streams to only include available streams (league, home, visitor)
 */
const filterAvailableStreams = (streams: StreamState[]): StreamState[] => {
  return streams.filter(
    (s) => s.type === 'league' || s.type === 'home' || s.type === 'visitor'
  )
}

/**
 * Get team image for a stream type
 */
const getStreamImage = (
  streamType: StreamType,
  homeTeamImage: string | null,
  visitorTeamImage: string | null
): string => {
  switch (streamType) {
    case 'home':
      return homeTeamImage || PLACEHOLDER_TEAM
    case 'visitor':
      return visitorTeamImage || PLACEHOLDER_TEAM
    case 'league':
    default:
      return PLACEHOLDER_ORGANIZATION
  }
}

/**
 * Dropdown component for selecting between multiple active streams
 */
const StreamSelectorDropdown = ({
  streams,
  selectedStreamType,
  onSelectStream,
  homeTeamName,
  visitorTeamName,
  homeTeamImage,
  visitorTeamImage,
  organizationName,
  isMobile = false,
}: StreamSelectorDropdownProps) => {
  const availableStreams = useMemo(
    () => filterAvailableStreams(streams),
    [streams]
  )

  /**
   * Handles stream option click
   */
  const handleSelect = useCallback(
    (streamType: StreamType) => {
      onSelectStream(streamType)
    },
    [onSelectStream]
  )

  /**
   * Render a single stream menu item
   */
  const renderMenuItem = useCallback(
    (stream: StreamState) => {
      const isSelected = selectedStreamType === stream.type
      const label = getStreamLabel(
        stream.type,
        homeTeamName,
        visitorTeamName,
        organizationName
      )
      const image = getStreamImage(stream.type, homeTeamImage, visitorTeamImage)

      return (
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: ${isMobile ? '8px' : '10px'};
            padding: ${isMobile ? '8px 12px' : '10px 14px'};
            cursor: pointer;
            transition: all 0.15s ease;
            &:hover {
              background: rgba(78, 85, 97, 0.5);
            }
          `}
          onClick={() => handleSelect(stream.type)}
        >
          {/* Team/League icon */}
          <div
            css={css`
              width: ${isMobile ? '28px' : '32px'};
              height: ${isMobile ? '28px' : '32px'};
              border-radius: 50%;
              background: ${colors.SECONDARY_300};
              border: 1px solid rgba(78, 85, 97, 0.8);
              display: flex;
              align-items: center;
              justify-content: center;
              overflow: hidden;
              flex-shrink: 0;
            `}
          >
            <img
              src={image}
              alt={label}
              css={css`
                width: 100%;
                height: 100%;
                object-fit: cover;
              `}
            />
          </div>

          {/* Name */}
          <Text
            variant='paragraphSmall'
            weight='semiBold'
            color={colors.WHITE}
            style={`
              flex: 1;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            `}
          >
            {label}
          </Text>

          {/* Status badge */}
          {stream.isLive && (
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 4px;
                padding: 2px 6px;
                background: ${colors.ERROR_200};
                border-radius: 3px;
              `}
            >
              <div
                css={css`
                  width: 5px;
                  height: 5px;
                  border-radius: 50%;
                  background: ${colors.WHITE};
                `}
              />
              <span
                css={css`
                  color: ${colors.WHITE};
                  font-size: 10px;
                  font-weight: 600;
                  text-transform: uppercase;
                  letter-spacing: 0.5px;
                `}
              >
                LIVE
              </span>
            </div>
          )}
          {!stream.isLive && stream.hasRecording && (
            <div
              css={css`
                display: flex;
                align-items: center;
                padding: 2px 6px;
                background: ${colors.INFO_400};
                border-radius: 3px;
              `}
            >
              <span
                css={css`
                  color: ${colors.WHITE};
                  font-size: 10px;
                  font-weight: 600;
                  text-transform: uppercase;
                  letter-spacing: 0.5px;
                `}
              >
                REPLAY
              </span>
            </div>
          )}

          {/* Checkmark for selected */}
          {isSelected && (
            <CheckOutlined
              css={css`
                color: ${colors.PRIMARY};
                font-size: 14px;
                flex-shrink: 0;
              `}
            />
          )}
        </div>
      )
    },
    [
      selectedStreamType,
      homeTeamName,
      visitorTeamName,
      homeTeamImage,
      visitorTeamImage,
      organizationName,
      isMobile,
      handleSelect,
    ]
  )

  /**
   * Dropdown menu items
   */
  const menuItems = useMemo(() => {
    return availableStreams.map((stream) => ({
      key: stream.type,
      label: renderMenuItem(stream),
    }))
  }, [availableStreams, renderMenuItem])

  return (
    <Dropdown
      menu={{
        items: menuItems,
        style: {
          background: 'rgba(39, 48, 62, 0.95)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(78, 85, 97, 0.6)',
          borderRadius: '8px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
          minWidth: isMobile ? '180px' : '220px',
          padding: '4px 0',
        },
      }}
      trigger={['click']}
      placement='bottomLeft'
      overlayStyle={{
        zIndex: 10003,
      }}
    >
      <button
        css={css`
          display: flex;
          align-items: center;
          gap: ${isMobile ? '4px' : '6px'};
          background-color: rgba(0, 0, 0, 0.6);
          padding: ${isMobile ? '4px 8px' : '6px 10px'};
          border-radius: 4px;
          border: 1px solid ${colors.NEUTRAL_50};
          cursor: pointer;
          transition: all 0.2s;
          &:hover {
            background-color: rgba(0, 0, 0, 0.8);
            border-color: ${colors.WHITE};
          }
        `}
      >
        <Icon
          name='exchange-alt'
          fontSize={isMobile ? 10 : 12}
          color={colors.WHITE}
        />
        <span
          css={css`
            color: ${colors.WHITE};
            font-size: ${isMobile ? '10px' : '12px'};
            font-weight: 600;
            letter-spacing: 0.5px;
          `}
        >
          {isMobile ? 'Switch' : 'Switch Stream'}
        </span>
      </button>
    </Dropdown>
  )
}

export default StreamSelectorDropdown
